import { Component, DetailEvent, Entity, Object3DSetEvent, registerComponent } from "aframe";
import { Bone, Object3D, Quaternion, SkinnedMesh, Vector3, Vector3Like } from "three";
import { AmmoBodyComponent } from "./ammo";
import { AnchorEntity } from "./anchor";
import { SetAnimationEventDetail } from "./avatar";
import { SlotAnimationsComponent } from "./slot-animations";
import { pathOf, traverseEntity } from "./utils";

export interface SlotComponentData
{
	type? : string; //type defines whether anchors fit this slot ; defaults to name if omitted.
	name : string; //unique name of this slot among its container entity
	item? : Entity;
	anchor? : AnchorEntity;
	label? : string;
	icon? : string;
	locked? : boolean;
	forward? : string[];
	accept? : string[];
}
export interface SlotEventDetail
{
	slot : SlotEntity;
	item : Entity;
	anchor : AnchorEntity;
}
export type SlotEvent = DetailEvent< SlotEventDetail >;


export interface SlotComponent extends Component
{
	isEntityConnection : boolean;
	previousProperties ? : { [attr:string] : { [prop:string] : any } };
	data : SlotComponentData;
	sqrDistanceFrom(item : Entity ) : number;
	el : SlotEntity;
}

interface SlotComponentImplementation extends SlotComponent
{
	childDetachedListener : EventListener;
	forwardListener : EventListener;
	attach( item : Entity, anchor? : AnchorEntity ) : void;
	detach( item : Entity, anchor? : AnchorEntity, forward? : string[] ) : void;
	updateSlotAnimation() : void; 
}

export type SlotEntity = Entity< { [attrName : string] : Component } & {
	"slot" : SlotComponent
} > & 
{
	slotItem? : Entity;
	slotAnchor? : AnchorEntity;
}




const preserveWorldMatrix = (obj : Object3D, newParent : Object3D) =>
{
	obj.updateWorldMatrix(true, false);
	newParent.updateWorldMatrix(true, false);
	const m = newParent.matrixWorld.clone().invert().multiply(obj.matrixWorld);
	m.decompose(obj.position, obj.quaternion, {} as Vector3);
};


const setAttributes = (el : Entity, attrName : string, newValues : { [key:string] : any }) =>
{
	const comp = el.components[attrName];
	const values : any = {};
	if(comp)
		for(let key in newValues)
			values[key] = comp.data[key];
	el.setAttribute(attrName, newValues);
	return values;
};


const findBone = (el: Entity, obj : Object3D & { el? : Entity; isBone? : boolean; }, name : string ) : Bone | undefined =>
{
	if(obj.el && (obj.el !== el)) return;
	if(obj.isBone && (obj.name == name)) return obj as Bone;
	for(let child of obj.children)
	{
		const found = findBone(el, child, name);
		if(found)
			return found;
	}
}





const squeezeMatrix = (mat:number[], scale:Vector3Like) =>
{
	for(let ix = 16; (ix -= 4) >= 0; )
	{
		mat[ix] *= scale.x;
		mat[ix+1] *= scale.y;
		mat[ix+2] *= scale.z;
	}
}
const unsqueezeMatrix = (mat:number[], scale:Vector3Like) =>
{
	for(let ix = 16; (ix -= 4) >= 0; )
	{
		mat[ix] /= scale.x;
		mat[ix+1] /= scale.y;
		mat[ix+2] /= scale.z;
	}
}





export interface SlotProxyComponent extends Component
{
	data : SlotEntity[];
}
export type SlotProxyEntity = Entity< { [attrName : string] : Component } & {
	"slot-proxy" : SlotProxyComponent
} >;
registerComponent("slot-proxy", {
	schema : { type : "selectorAll" }
});





registerComponent("slot", {
	
	schema : {
		type : { type : "string" },
		name : { type : "string" },
		item : { type : "selector" },
		anchor : { type : "selector" },
		label : { type : "string" },
		icon : { type : "string" },
		locked : { type : "boolean", default : false},
		forward : { type : "array", default : [] }, //Events to listen on item and emit on slot.
		accept : { type : "array", default : [] } //CSS Selectors that the anchor must match to fit in.
	},
	
	init(this : SlotComponentImplementation)
	{
		this.isEntityConnection = true;

		this.childDetachedListener = ((event : Event & { detail : { el : any } }) =>
		{
			if(event.target !== this.el)
				return;
			if(event.detail.el !== this.data.item)
				return;

			this.el.setAttribute(this.attrName, { item : null, anchor : null } );
		}) as EventListener;
		this.el.addEventListener("child-detached", this.childDetachedListener);

		this.forwardListener = (event : Event & { detail ? : any } ) =>
		{
			const forwarded : CustomEvent & { cause ? : Event } = new CustomEvent(event.type, { detail : event.detail, bubbles : event.bubbles, cancelable : event.cancelable, composed : event.composed });
			forwarded.cause = event;

			if(event.cancelable && event.defaultPrevented)
				forwarded.preventDefault();
			
			if(!this.el.dispatchEvent(forwarded))
				event.preventDefault();
		};
		// Doesn't work : slot is being hidden while starting an animation clip
		//this.el.addEventListener("visible-changed", () => this.updateSlotAnimation() );
	},

	remove(this : SlotComponentImplementation)
	{
		this.el.removeEventListener("child-detached", this.childDetachedListener);
		if(!this.data) return; //What?
		const el = this.data.item, anchor = this.data.anchor;
		el && anchor && this.detach(el, anchor, this.data.forward);
	},

	getConnectedEntities(this : SlotComponent)
	{
		//return this.data.anchor ? [this.data.anchor] : [];
		return (this.data.anchor && this.data.item) ? [this.data.item, this.data.anchor] : [];
	},

	sqrDistanceFrom(item : AnchorEntity )
	{
		const slotMatrix = this.el.object3D.matrixWorld.elements;
		const itemMatrix = item.object3D.matrixWorld.elements;
		const dx = slotMatrix[12] - itemMatrix[12], dy = slotMatrix[13] - itemMatrix[13], dz = slotMatrix[14] - itemMatrix[14];
		/* TODO const len = item.components.anchor && item.components.anchor.data.length;
		if( len > 0 ) //Segment anchor
		{
			const dot = dx * itemMatrix[4] + dy * itemMatrix[5] + dz * itemMatrix[6];
			if(dot >= len)
				return (dx * dx + dy * dy + dz * dz - len * len);
			if(dot >= 0)
				return (dx * dx + dy * dy + dz * dz - dot * dot);
		} */
		return (dx * dx + dy * dy + dz * dz);
	},



	attach(this : SlotComponentImplementation, itemEl : Entity, anchorEl : AnchorEntity)
	{
		const thisEl = this.el, previousProps = this.previousProperties!;
		const ownerEl = thisEl.parentEl;
		const aComp = anchorEl.components.anchor;

		thisEl.slotItem = itemEl;
		thisEl.slotAnchor = anchorEl;

		//First of all : attach Entity. This will trigger previous slot's detach if required.
		if(itemEl.parentEl !== thisEl) //el may already be a child of thisEl
			thisEl.add(itemEl);

		//Add event forwarding listeners :
		if(this.data.forward)
			for(let type of this.data.forward)
				itemEl.addEventListener(type, this.forwardListener);

		aComp.setSlotPosition(itemEl);

		//Set new body properties, and store previous ones
		const ammoBody = itemEl.components["ammo-body"] as Component;
		if(ammoBody)
		{
			let newProps : { [key:string] : any } = {
				type:"kinematic", 
				collisionGroup : aComp.data.collisionGroup,
				collisionMask : aComp.data.collisionMask 
			};

			//Turn on item inertia if this slot is handled by an XR Input
			if( thisEl.sceneEl!.is('vr-mode') )
				for(let key in thisEl.components)
					if(thisEl.components[key].name == 'xr-input')
					{
						newProps.inertia = true;
						break;
					}

			previousProps["ammo-body"] = setAttributes(itemEl, "ammo-body", newProps);
		}

		//Attach skeleton if needed
		if(aComp && aComp.data.skeleton)
		{
			const getOwnBone = (b : Object3D) =>
			{
				const ownBone = (ownerEl && findBone(ownerEl, ownerEl.object3D, b.name))
					|| findBone(thisEl, thisEl.object3D, b.name);
				if(ownBone)
					return ownBone;

				const clone = b.clone(false);
				if(b.parent)
				{
					const ownParent = getOwnBone(b.parent);
					ownParent!.add(clone);
				}
				return clone;
			};


			const traverseSkeletons = (o:Object3D) => {
				const sk = (o as SkinnedMesh).skeleton;
				if(sk)
					sk.bones = sk.bones.map(b => getOwnBone(b) as Bone);
			};


			const traverseListener = (event : Object3DSetEvent) =>
			{
				traverseEntity(itemEl, traverseSkeletons, event.detail.object);
			};
			itemEl.addEventListener("object3dset", traverseListener);
			anchorEl.addEventListener("detached-from-slot", (_) =>
			{
				itemEl.removeEventListener("object3dset", traverseListener as any);
			}, { once : true });

			traverseEntity(itemEl, traverseSkeletons);
		}
			
		
		//Squeeze bones if needed
		if(aComp && aComp.data.squeezeBones.length && ownerEl)
			traverseEntity(ownerEl, (obj : Object3D) => {
				const sk = (obj as SkinnedMesh).skeleton;
				if(!sk) return;

				sk.boneInverses = sk.boneInverses.map( m => m.clone() );
				const re = new RegExp(aComp.data.squeezeBones);
				for(let ix = sk.bones.length; --ix >= 0; )
				{
					if(re.test( pathOf(sk.bones[ix], ownerEl.object3D )))
						squeezeMatrix(sk.boneInverses[ix].elements, aComp.data.squeezeScale);
				}
			} );


		//Emit Slot-specific events
		const attachedEventDetail : SlotEventDetail = { slot : thisEl, item : itemEl, anchor: anchorEl };
		thisEl.emit("item-attached", attachedEventDetail);
		anchorEl.emit("attached-to-slot", attachedEventDetail);

		//Emit Generic connection events
		thisEl.emit("connected-to-entity", {entity : itemEl, connection : this} );
		thisEl.emit("connected-to-entity", {entity : anchorEl, connection : this} );
		anchorEl.emit("connected-to-entity", {entity : thisEl, connection : aComp } );

		this.updateSlotAnimation();
	},

	updateSlotAnimation(this : SlotComponentImplementation) 
	{
		const thisEl = this.el;
		const anchorEl = thisEl.object3D.visible && this.data.anchor;
		const eventDetail : SetAnimationEventDetail = { level : 20, animations : {} };
		if(anchorEl)
		{
			//Merge animation rules from the anchor (slot-specific then generic) and the slot :
			const slotAnimations = [
				(anchorEl.components["slot-animations__" + (this.data.type || "")] as SlotAnimationsComponent),
				(anchorEl.components["slot-animations__" + this.data.name] as SlotAnimationsComponent),
				(anchorEl.components["slot-animations"] as SlotAnimationsComponent),
				(thisEl.components["slot-animations"] as SlotAnimationsComponent) 
			].map( x => (x && x.data) || {} );

			//Insert animations from last to first, only if not present in an earlier stage :
			for(let ix = slotAnimations.length; --ix >= 0; )
			{
				const map = slotAnimations[ix];
				for(let key in map)
				{
					let jx = ix;
					while(--jx >= 0)
						if(key in slotAnimations[jx])
							break;
					if(jx >= 0)
						continue;
					eventDetail.animations[key] = map[key];
				}
			}

			/*
			for(let slotAnimComp of slotAnimations)
				if(slotAnimComp && slotAnimComp.data)
					for(let key in slotAnimComp.data)
					{
						if( key in eventDetail.animations ) continue;
						eventDetail.animations[key] = slotAnimComp.data[key];
					} */
		}
		return thisEl.emit("set-animation", eventDetail )

	},

	detach(this : SlotComponentImplementation, item : Entity, anchor : AnchorEntity, forward? : string[] )
	{
		if(item === this.el.slotItem)
			delete this.el.slotItem;
		if(anchor === this.el.slotAnchor)
			delete this.el.slotAnchor;

		//Remove forwarding listeners :
		if(forward)
			for(let type of forward)
				item.removeEventListener(type, this.forwardListener);

		//Restore previous properties
		if(this.previousProperties)
		{
			for(let attr in this.previousProperties)
				item.setAttribute(attr, this.previousProperties[attr]);
			delete this.previousProperties;
		}

		//Restore skeleton
		const anchorData = anchor.components.anchor && anchor.components.anchor.data;
		anchorData && anchorData.skeleton && traverseEntity(item, (o:Object3D) => {
			const sk = (o as SkinnedMesh).skeleton;
			if(!sk) return;
			sk.bones = sk.bones.map( b => findBone(item, item.object3D, b.name) || b );
		});

		//Restore squeezed bones
		const ownerEl = this.el.parentEl!;
		anchorData && anchorData.squeezeBones.length && ownerEl && traverseEntity(ownerEl, (obj : Object3D) => {
			const sk = (obj as SkinnedMesh).skeleton;
			if(!sk) return;

			sk.boneInverses = sk.boneInverses.map( m => m.clone() );
			const re = new RegExp(anchorData.squeezeBones);
			for(let ix = sk.bones.length; --ix >= 0; )
			{
				if(re.test( pathOf(sk.bones[ix], ownerEl.object3D) ))
					unsqueezeMatrix(sk.boneInverses[ix].elements, anchorData.squeezeScale);
			}
		} );


		if( item.isConnected && (item.parentEl === this.el) ) // aka not already attached to another slot
		{
			//Restore real parent
			const realParent = item.parentElement as Entity;
			if(realParent && (item.parentEl !== realParent) && realParent.isConnected)
			{
				preserveWorldMatrix(item.object3D, realParent.object3D);
				realParent.add( item );
				item.object3D.updateWorldMatrix(true, true);

				const body = item.components["ammo-body"] as AmmoBodyComponent;
				if(body)
				{
					const p = new Vector3(), q = new Quaternion();
					p.setFromMatrixPosition(item.object3D.matrixWorld);
					q.setFromRotationMatrix(item.object3D.matrixWorld);
					body.moveTo(p, q);
				}
			}
		}




		//Emit Slot-specific events
		const detachedDetail : SlotEventDetail = { slot : this.el, item, anchor };
		this.el.emit("item-detached", detachedDetail);
		anchor.emit("detached-from-slot", detachedDetail);

		//Emit Generic connection events
		this.el.emit("disconnected-from-entity", { entity : item, connection : this } );
		this.el.emit("disconnected-from-entity", { entity : anchor, connection : this } );
		anchor.emit("disconnected-from-entity", { entity : this.el, connection : anchor.components.anchor } );

		this.updateSlotAnimation();
	},

	

	update(this : SlotComponentImplementation, oldData : SlotComponentData)
	{
		const el = this.data.item, oldEl = oldData && oldData.item;
		const anchor = this.data.anchor, oldAnchor = oldData && oldData.anchor;

		if(this.data.name != oldData?.name)
			this.el.emit("slot-initialized", this.data.name); //Dispatch an event for anchors waiting to attach
		

		if(oldData && (el === oldEl) && (anchor === oldAnchor)) //Same attachment : just update event forwarding
		{
			if(el && oldData.forward)
				for(let type of oldData.forward)
					el.removeEventListener(type, this.forwardListener);
			if(el && this.data.forward)
				for(let type of this.data.forward)
					el.addEventListener(type, this.forwardListener);
			return;
		} 

		if(oldEl /*&& (oldEl.parentEl === this.el)*/ ) //Release an item
		{
			this.detach(oldEl, oldAnchor || anchor, oldData.forward || this.data.forward );
		}

		if(el) //Attach an item
		{
			this.previousProperties = {};

			if(!anchor) //Anchor not defined : attach first fitting and enabled anchor.
			{
				const anchors = Array.from(el.querySelectorAll<AnchorEntity>("[anchor]"));
				for(let a of anchors)
				{
					const aData = a.components.anchor && a.components.anchor.data;
					if(aData && aData.enabled && aData.attach && a.components.anchor.fits(this.el) )
					{
						this.el.setAttribute(this.attrName, {anchor : a} );
						break; 
					}
				}
				return;
			}

			if( !anchor.components.anchor )
			{
				const listener = (event : Event) =>
				{
					if(anchor !== this.data.anchor)
					{
						anchor.removeEventListener("componentinitialized", listener);
						return;
					}
					if(anchor.components.anchor)
					{
						this.attach(el, anchor);
						anchor.removeEventListener("componentinitialized", listener);
					}
				};
				return anchor.addEventListener("componentinitialized", listener);
			}

			this.attach(el, anchor);
			
		}
	}
});

